/**
 * src/hooks/useChat.ts
 * ----------------------
 * React hook that orchestrates sending messages and streaming responses.
 *
 * Fully implemented this will:
 *   - Read messages, isTyping, error from useChatStore.
 *   - Expose sendMessage(content, files?) which:
 *       1. Appends an optimistic user message to the store.
 *       2. Opens an SSE stream via streamMessage() from src/api/chat.ts.
 *       3. Dispatches each StreamEvent to chatStore.handleStreamEvent().
 *       4. Handles stream cleanup on component unmount.
 *   - Expose retryLastMessage() to resend the last user message.
 *   - Expose loadHistory(sessionId) to populate messages from API.
 *   - Expose handleAction(actionId, value) to handle quick-reply clicks.
 *   - Expose handleReact(messageId, reaction) to submit a reaction.
 *
 * Key dependencies:
 *   src/stores/chat.ts, src/api/chat.ts, src/stores/session.ts,
 *   src/types/message.ts
 */
import { useCallback, useEffect, useRef } from 'react'
import { useChatStore } from '@/stores/chat'
import { useSessionStore } from '@/stores/session'
import { streamMessage, getMessages, reactToMessage } from '@/api/chat'
import type { Reaction } from '@/types/message'
import { createStreamPresenter } from '@/lib/streamPresenter'

export function useChat() {
  const messages = useChatStore((state) => state.messages)
  const isTyping = useChatStore((state) => state.isTyping)
  const error = useChatStore((state) => state.error)
  const appendMessage = useChatStore((state) => state.appendMessage)
  const handleStreamEvent = useChatStore((state) => state.handleStreamEvent)
  const setReactionLocal = useChatStore((state) => state.setReaction)
  const loadMessages = useChatStore((state) => state.loadMessages)
  const clearMessages = useChatStore((state) => state.clearMessages)
  const setError = useChatStore((state) => state.setError)
  const activeSessionId = useSessionStore((state) => state.activeSessionId)
  const esRef = useRef<EventSource | null>(null)
  const presenterRef = useRef<ReturnType<typeof createStreamPresenter> | null>(null)
  useEffect(() => () => {
    esRef.current?.close()
    presenterRef.current?.cancel()
  }, [activeSessionId])

  const sendMessage = useCallback(
    async (content: string) => {
      if (!activeSessionId || !content.trim()) return

      // Optimistic user message
      const userMsg = {
        id: crypto.randomUUID(),
        sessionId: activeSessionId,
        role: 'user' as const,
        content,
        createdAt: new Date().toISOString(),
      }
      appendMessage(userMsg)

      // Placeholder streaming message
      const streamingId = crypto.randomUUID()
      appendMessage({
        id: streamingId,
        sessionId: activeSessionId,
        role: 'assistant',
        content: '',
        createdAt: new Date().toISOString(),
        isStreaming: true,
      })

      // Store streaming ID for event reducer
      useChatStore.setState({ streamingMessageId: streamingId, isTyping: true, error: null })

      // Open SSE stream
      esRef.current?.close()
      presenterRef.current?.cancel()
      const presenter = createStreamPresenter((event) => {
        if (useSessionStore.getState().activeSessionId === activeSessionId && useChatStore.getState().streamingMessageId === streamingId) handleStreamEvent(event)
      }, { reducedMotion: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false })
      presenterRef.current = presenter
      esRef.current = streamMessage(
        activeSessionId,
        content,
        (event) => {
          presenter.push(event)
        },
        () => {
          presenter.push({ type: 'error', error: 'Connection lost. Please try again.' })
        },
      )
    },
    [activeSessionId, appendMessage, handleStreamEvent],
  )

  const loadHistory = useCallback(
    async (sessionId: string) => {
      clearMessages()
      try {
        const { messages } = await getMessages(sessionId)
        loadMessages(messages)
      } catch {
        setError('Could not load message history.')
      }
    },
    [clearMessages, loadMessages, setError],
  )

  // Pull the authoritative server history (used to receive live agent replies
  // during human takeover, and to detect an auto-closed session). Skips while
  // the AI is actively streaming so it never clobbers an in-progress reply.
  // Returns the session's close state so the UI can request CSAT feedback.
  const refreshMessages = useCallback(
    async (sessionId: string): Promise<{ closed: boolean; requestFeedback: boolean } | undefined> => {
      const { isTyping: typing, streamingMessageId } = useChatStore.getState()
      if (typing || streamingMessageId) return undefined
      try {
        const { messages, status, request_feedback } = await getMessages(sessionId)
        loadMessages(messages)
        return {
          closed: status != null && status !== 'active',
          requestFeedback: Boolean(request_feedback),
        }
      } catch { /* ignore poll errors */ }
      return undefined
    },
    [loadMessages],
  )

  const handleReact = useCallback(
    async (messageId: string, reaction: Reaction) => {
      if (!activeSessionId) return
      setReactionLocal(messageId, reaction)
      await reactToMessage(activeSessionId, messageId, reaction)
    },
    [activeSessionId, setReactionLocal],
  )

  const handleAction = useCallback(
    (_actionId: string, value: string) => {
      sendMessage(value)
    },
    [sendMessage],
  )

  return {
    messages,
    isTyping,
    error,
    sendMessage,
    loadHistory,
    refreshMessages,
    handleReact,
    handleAction,
  }
}
